/**
 * Preferencias de accesos directos del sidebar (ids de panelNav).
 * Cache en localStorage + sync con /perfil/sidebar-pins.
 */
import { reactive } from 'vue';
import axios from 'axios';
import { getLogin } from '@/utils/session';

export const SIDEBAR_PINS_MAX = 8;
export const SIDEBAR_FIXED_IDS = new Set(['home', 'perfil', 'cuenta_vendedor']);

const storageKey = () => `sidebar_pins:${getLogin() || 'anon'}`;

/** @type {{ pins: string[]|null, loaded: boolean, loading: boolean }} */
export const sidebarPinsState = reactive({
    pins: null,
    loaded: false,
    loading: false,
});

function readLocal() {
    try {
        const raw = localStorage.getItem(storageKey());
        if (raw == null) return undefined;
        if (raw === 'null') return null;
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed.map(String) : null;
    } catch {
        return undefined;
    }
}

function writeLocal(pins) {
    try {
        if (pins == null) localStorage.setItem(storageKey(), 'null');
        else localStorage.setItem(storageKey(), JSON.stringify(pins));
    } catch {
        /* ignore quota */
    }
}

export function setSidebarPinsLocal(pins) {
    sidebarPinsState.pins = pins == null ? null : [...pins];
    sidebarPinsState.loaded = true;
    writeLocal(sidebarPinsState.pins);
}

export function getSidebarPins() {
    return sidebarPinsState.pins;
}

/** Carga cache local y luego el servidor (best-effort). */
export async function ensureSidebarPinsLoaded() {
    if (sidebarPinsState.loading) return sidebarPinsState.pins;
    if (!sidebarPinsState.loaded) {
        const local = readLocal();
        if (local !== undefined) {
            sidebarPinsState.pins = local;
            sidebarPinsState.loaded = true;
        }
    }
    if (!getLogin()) return sidebarPinsState.pins;
    sidebarPinsState.loading = true;
    try {
        const { data } = await axios.get('/perfil/sidebar-pins');
        const pins = data && Object.prototype.hasOwnProperty.call(data, 'pins') ? data.pins : null;
        setSidebarPinsLocal(Array.isArray(pins) ? pins : null);
    } catch {
        if (!sidebarPinsState.loaded) {
            sidebarPinsState.pins = null;
            sidebarPinsState.loaded = true;
        }
    } finally {
        sidebarPinsState.loading = false;
    }
    return sidebarPinsState.pins;
}

export async function saveSidebarPins(pins) {
    const normalized = pins == null ? null : [...pins];
    const { data } = await axios.put('/perfil/sidebar-pins', { pins: normalized });
    const saved = data && Object.prototype.hasOwnProperty.call(data, 'pins') ? data.pins : normalized;
    setSidebarPinsLocal(Array.isArray(saved) ? saved : null);
    return sidebarPinsState.pins;
}

export async function resetSidebarPins() {
    return saveSidebarPins(null);
}
